// @flow
import React, {useEffect, useState} from "react";
import type {Product} from "../../../models/Product";
import type {translate} from "../../../i18next";
import {useForm} from "react-hook-form";
import type {ProductEnquiry} from "../../../models/ProductEnquiry";
import {WAY_TO_CONTACT} from "../../../models/ProductEnquiry";
import type {ContactData} from "../../../models/ContactData";
import {toast} from "react-toastify";
import SlCaptcha from "../../SlCaptcha";
import "./QuestionForm.scss";
import QuestionProductView from "./QuestionProductView";
import {BrandContext} from "../../../preview/App";
import LocationChoiceField, {LOCATION_FIELD} from "../../pickupLocation/LocationChoiceField";
import type {GroupMember} from "../../../models/GroupMember";

export type QuestionFormProps = {
    product: Product,
    closeQuestion: () => void,
    resetMailing: () => void,
    t: translate,
    submitEnquiry: (ProductEnquiry) => void,
    contactData: ContactData,
    sendSuccess: boolean,
    sendError: boolean,
    captchaInvalid: boolean,
    privacyNoteLinkUrl: ?string,
    locationChoices: ?GroupMember[],
    pickupLocation: ?GroupMember,
    isShopEnabled: boolean,
}

const QuestionForm = (props: QuestionFormProps) => {
    const brand = React.useContext(BrandContext);
    const [captcha, setCaptcha] = useState(null);

    const {
        register,
        handleSubmit,
        formState: {errors},
        watch,
        clearErrors
    } = useForm({
        defaultValues: {
            ...props.contactData,
            [LOCATION_FIELD]: props.pickupLocation ? props.pickupLocation.cfgKey : "",
        }
    });
    const onSubmit = (data: ProductEnquiry) => {
        props.submitEnquiry({
            ...data,
            product: props.product,
            captcha: captcha
        });
    };

    useEffect(() => {
        if (props.sendSuccess) {
            toast(props.t("feedback.relatedQuestions.sendSuccess"));
            props.closeQuestion();
            props.resetMailing();
        }
        if (props.sendError || props.captchaInvalid) {
            toast(props.sendError
                ? props.t("feedback.relatedQuestions.sendError")
                : props.t("feedback.captchaInvalid")
            );
            props.resetMailing();
        }
    });

    return <div className="hpm-question">
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div
                className={"hpm-close-question"}
                onClick={() => props.closeQuestion()}
                onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                        props.closeQuestion();
                    }
                }}
                role="button"
                tabIndex={0}
            />
            <QuestionProductView product={props.product} t={props.t}/>
            <div>
                <table cellPadding="0" cellSpacing="0">
                    <tbody>
                    <tr>
                        <td colSpan="2" className="hpm-questionHeadline">
                            <h3>{props.t("feedback.relatedQuestions.sharingHeadline")}</h3></td>
                    </tr>
                    <tr>
                        <td colSpan="2"><p>{props.t("feedback.relatedQuestions.sharingText")}</p></td>
                    </tr>
                    <tr>
                        <td className="hpm-label">
                            <span>{props.t("feedback.relatedQuestions.product")}:</span>
                        </td>
                        <td>
                            <span className="subject">{`"${props.product.name}"`}</span>
                        </td>
                    </tr>
                    <LocationChoiceField
                        locationChoices={props.locationChoices}
                        pickupLocation={props.pickupLocation}
                        isShopEnabled={props.isShopEnabled}
                        register={register}
                        hasError={Boolean((errors || {})[LOCATION_FIELD])}
                        t={props.t}
                    />
                    <tr>
                        <td colSpan="2" className={(errors || {}).message ? "hpm-error" : ""}>
                            <label htmlFor={"message"} className="hpm-label">{props.t("feedback.message")}</label>
                            <textarea id={"message"} name="message" {...register("message", {required: false})}></textarea>
                        </td>
                    </tr>
                    <tr>
                        <td colSpan="2">
                            <p>{props.t("feedback.mayWeContact")}</p>
                            <div className="wayOfContact">
                                <input id="contact-02" name="wayToContact" type="radio" value={WAY_TO_CONTACT.viaMail}
                                       {...register("wayToContact")} onClick={() => clearErrors("phone")}/>
                                <label htmlFor="contact-02">{props.t("feedback.email")}</label>
                                <input id="contact-01" name="wayToContact" type="radio" value={WAY_TO_CONTACT.viaPhone}
                                       {...register("wayToContact")} onClick={() => clearErrors("email")}/>
                                <label htmlFor="contact-01">{props.t("feedback.phone")}</label>
                            </div>
                            <div className={(errors || {}).name ? "hpm-error" : ""}>
                                <input type="text" placeholder={props.t("feedback.name")}
                                       name="name" id="name" {...register("name", {required: true})}/>
                            </div>
                            <div className={(errors || {}).phone ? "hpm-error" : ""}>
                                <input type="text" placeholder={props.t("feedback.phone")} name="phone"
                                       {...register("phone", {required: watch("wayToContact") === WAY_TO_CONTACT.viaPhone})}/>
                            </div>
                            <div className={(errors || {}).email ? "hpm-error" : ""}>
                                <input type="email" placeholder={props.t("feedback.email")}
                                       name="email"
                                       {...register("email", {
                                           required: watch("wayToContact") === WAY_TO_CONTACT.viaMail || watch("mailMyself"),
                                           pattern: {value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i}
                                       })}/>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td colSpan="2">
                            <table className="hpm-conditions">
                                <tbody>
                                <tr>
                                    <td>
                                        <input id="data-copy" name="mailMyself"
                                               type="checkbox" {...register("mailMyself")}/>
                                        <label htmlFor="data-copy">
                                            {props.t("feedback.copyCheckbox")}
                                        </label>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </td>
                    </tr>
                    <tr>
                        <td colSpan="2" className="hpm-privacy-note">
                            <p className="hpm-privacy-heading">{props.t("feedback.privacyNoteHeadline")}</p>
                            <p className="hpm-privacy-text">
                                {(() => {
                                    const text = props.t("feedback.privacyNoteText", {brand: brand ?? ""});
                                    const linkLabel = props.t("feedback.privacyNoteLink");
                                    const parts = text.split("[[LINK]]");
                                    return <React.Fragment>
                                        {parts[0]}
                                        {props.privacyNoteLinkUrl
                                            ? <a className="hpm-external-link"
                                                 href={props.privacyNoteLinkUrl}
                                                 target="_blank"
                                                 rel="noopener noreferrer">{linkLabel}</a>
                                            : linkLabel}
                                        {parts[1] || ""}
                                    </React.Fragment>;
                                })()}
                            </p>
                        </td>
                    </tr>
                    <tr>
                        <td colSpan="2" className={"captchaText"}>{props.t("feedback.enterCaptcha")}:</td>
                    </tr>
                    <tr>
                        <td colSpan={"2"}>
                            <div>
                                <SlCaptcha
                                    setCaptcha={setCaptcha}
                                    captcha={captcha}
                                    error={props.captchaInvalid}
                                />
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
                <button type="submit" className="hpm-contact-send">
                    {props.t("feedback.relatedQuestions.send")}
                </button>
            </div>
        </form>
    </div>;
};

export default QuestionForm;
